// @flow
import React, { Component, Fragment } from "react";
import classNames from "classnames";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import { withStyles } from "@material-ui/core/styles";

import type { ErrorsType } from "../../types/UserFormType";
import { ErrosDisplay, setErrorMsg } from "../../containers/ErrosDisplay";
import {
  ButtonSelect,
  StyledSelect,
  StyledTextField,
  CancelButton,
  SubmitButton,
  SingleRadioButton
} from "imagus-components";
import LicenceItemRedux from "../../containers/licences/LicenceItemRedux";
import CompanyNameReminder from "../../components/companies/CompanyNameReminder";
import AuthorisedLabel from "../companies/AuthorisedLabel";
import UpgradeQuoteTable from "./UpgradeQuoteTable";
import {
  CompileDatabaseItemtoJsItem,
  getQuoteNotes,
  checkItemPriceRange,
  calculateRetailPrice,
  displayRetailPrice,
  totalPriceDescription,
  PriceFormat2Number,
  getRealPrice,
  getRetailSumFromTable,
  getTotalPrices
} from "./QuoteCompileFunctions";
import {
  StartDatetoEndDate,
  JsDateObjectDisplayDateOnly,
  isMomentBefore,
  TodaytoStartTimeAppend
} from "../../../global/functions/DateTimeAmend";

const styles = (theme: *): * => ({
  paper: {
    paddingTop: theme.spacing.unit * 1,
    paddingBottom: theme.spacing.unit * 1,
    paddingLeft: theme.spacing.unit * 1,
    paddingRight: theme.spacing.unit * 1,
    marginTop: theme.spacing.unit * 2
  },
  hidden: {
    display: "none"
  },
  bottom: {
    marginTop: theme.spacing.unit * 3,
    display: "flow-root"
  },
  leftButton: {
    float: "left"
  },
  rightButton: {
    float: "right"
  },
  container: {
    paddingTop: theme.spacing.unit * 3,
    paddingBottom: theme.spacing.unit * 3,
    paddingLeft: theme.spacing.unit * 3,
    paddingRight: theme.spacing.unit * 3
  },
  labelFormControl: {
    padding: "10px 5px",
    fontSize: "0.9375rem"
  },
  greenLabel: {
    color: theme.palette.primary.light,
    "&$labelFocused": {
      color: theme.palette.primary.main
    },
    "&$labelError": {
      color: theme.palette.error.main
    },
    "&$labelDisabled": {
      color: theme.palette.grey.main
    }
  },
  labelFocused: {},
  textFieldRoot: {
    "label + &": {
      marginTop: theme.spacing.unit * 3
    }
  }
});

type UpgradeQuoteFormPropsType = {
  classes: { [key: string]: * },
  onClose: Function,
  companyType: string,
  LicenceList: Array<*>,
  fetchLicenceDetail: Function,
  fetchRealExpiryDate: Function,
  fetchExpiryDate: Function,
  initials: *,
  createQuote: Function,
  editQuote: Function,
  priceSheet: *,
  isAdmin?: boolean,
  isEdit?: boolean
};

type UpgradeQuoteFormStateType = {
  errors: ErrorsType,
  loading: boolean,
  formIndex: number,
  licenceId: string,
  licence: { [key: string]: * } | null,
  faceNo: number | null,
  streamNo: number | null,
  yearNo: number | null,
  startDate: Date,
  finalExpiryDate: Date | null,
  renewDays: number,
  isOverDue: boolean,
  startDateOption: boolean
};

const initialform: UpgradeQuoteFormStateType = {
  errors: {},
  loading: false,
  formIndex: 1,
  licenceId: "",
  licence: null,
  faceNo: null,
  streamNo: null,
  yearNo: null,
  startDate: TodaytoStartTimeAppend(true),
  finalExpiryDate: null,
  renewDays: 0,
  isOverDue: false,
  startDateOption: false
};

class UpgradeQuoteForm extends Component<
  UpgradeQuoteFormPropsType,
  UpgradeQuoteFormStateType
> {
  csvArray: Array<*>;
  constructor(props: UpgradeQuoteFormPropsType) {
    super(props);
    this.state = initialform;
  }

  componentDidMount() {
    this.csvArray = new Array(7);
    this.setState(
      { ...initialform, ...this.props.initials, errors: {} },
      this.setExpiryDate
    );
  }

  handlePageSwitch = (name: string, value: number): * => {
    this.setState({ [name]: value });
  };

  handleSelectChange = (e: SyntheticInputEvent<*>): * => {
    const name = e.target.name;
    const value = e.target.value;

    this.setState({ [name]: value }, (): * => {
      if (name === "licenceId") {
        this.setState(
          {
            licence: this.props.fetchLicenceDetail(this.state.licenceId)
          },
          (): * => {
            this.setExpiryDate();
          }
        );
      }
    });
  };

  handleQtyChange = (fieldName: string): * => (
    e: SyntheticInputEvent<*>
  ): * => {
    const value = e.target.value;
    const name = e.target.name;
    this.setState(
      {
        [name]: value
      },
      (): * => {
        this.checkQtyValue(fieldName);
        if (name === "yearNo") {
          this.setExpiryDate();
        }
      }
    );
  };

  syncDateChange = (name: string, date: Date): * => {
    this.setState({ [name]: date }, (): * => {
      if (name === "startDate") {
        this.setExpiryDate();
      }
    });
  };

  handleChange = (e: SyntheticInputEvent<*>): * => {
    const name = e.target.name;
    const value = e.target.value;

    if (name === "startDateOption") {
      this.setState(
        { startDateOption: value === "true" ? true : false },
        (): * => {
          let startDate = TodaytoStartTimeAppend(true);
          if (this.state.startDateOption) {
            startDate = this.props.fetchRealExpiryDate(this.state.licence);
          }

          this.setState({ startDate: startDate }, this.setExpiryDate);
        }
      );
    } else {
      this.setState({ [name]: value }, this.setExpiryDate);
    }
  };

  checkValueValidation = (partNo: string, value: string): Function => {
    let priceCheck = false;

    const productSheet = this.state.licence.company.inheritProductSheet;
    const priceList = this.props.priceSheet.parts.edges;

    let index = priceList.findIndex(
      (x: *): Function => x.node.partNo === partNo
    );

    if (index > -1) {
      const node = priceList[index].node;

      const sheetPart = this.getProductSheetPart(node.partNo, productSheet);
      const item = CompileDatabaseItemtoJsItem(node, sheetPart);

      priceCheck = checkItemPriceRange(value, item.min, item.max);
    }

    return priceCheck;
  };

  checkQtyValue = (name: string): * => {
    let errorName;
    let partNo;

    if (name === "faceNo") {
      partNo = "IMQ-SE-003a";
      errorName = "facebase";
    } else if (name === "streamNo") {
      partNo = "IMQ-SE-003b";
      errorName = "project";
    } else if (name === "yearNo") {
      partNo = "IMQ-SM-003";
      errorName = "quote";
    }

    const priceCheck = this.checkValueValidation(partNo, this.state[name]);

    if (!priceCheck) {
      this.setState({
        errors: setErrorMsg(
          this.state.errors,
          errorName,
          name + " is not valid"
        )
      });
    } else {
      this.setState({
        errors: setErrorMsg(this.state.errors, errorName, "", true)
      });
    }
  };

  setExpiryDate = (): * => {
    let renewDays = 0;
    let finalExpiryDate = null;
    let isOverDue = false;
    if (this.state.licence && this.state.startDate) {
      const expiryList = this.props.fetchExpiryDate(
        this.state.licence,
        this.state.startDate,
        this.state.yearNo
      );
      renewDays = expiryList[0];
      finalExpiryDate = expiryList[1];
      isOverDue = expiryList[2];
    }

    this.setState({
      renewDays: renewDays,
      finalExpiryDate: finalExpiryDate,
      isOverDue: isOverDue
    });
  };

  submit = async (): * => {
    this.setState((): * => ({ loading: true }));

    let parts = [];
    this.csvArray
      .filter((x: *): Function => x[0] !== "total")
      .forEach((row: string): * => {
        parts.push({
          partNo: row[0],
          quantity: row[1],
          quotedRetailPrice: PriceFormat2Number(row[2]),
          quotedDistributerPrice: PriceFormat2Number(row[3]),
          quotedResellerPrice: PriceFormat2Number(row[4])
        });
      });

    if (this.props.isEdit) {
      this.props.editQuote(
        PriceFormat2Number(this.csvArray[6][2]),
        PriceFormat2Number(this.csvArray[6][3]),
        PriceFormat2Number(this.csvArray[6][4]),
        parts,
        this.state.startDate,
        this.state.finalExpiryDate,
        this.submitDone,
        this.submitFail
      );
    } else {
      this.props.createQuote(
        PriceFormat2Number(this.csvArray[6][2]),
        PriceFormat2Number(this.csvArray[6][3]),
        PriceFormat2Number(this.csvArray[6][4]),
        parts,
        this.state.licenceId,
        this.state.startDate,
        this.state.finalExpiryDate,
        this.submitDone,
        this.submitFail
      );
    }
  };

  submitDone = () => {
    this.props.onClose();
  };

  submitFail = (errors: ErrorsType) => {
    this.setState({ errors: errors, loading: false });
  };

  getProductSheetPart = (partNo: string, productSheet: *): * => {
    let sheetPart = null;
    if (productSheet) {
      let index =
        productSheet.sheetParts.edges &&
        productSheet.sheetParts.edges.findIndex(
          (item: *): * => item.node.part.partNo === partNo
        );
      if (index > -1) {
        sheetPart = productSheet.sheetParts.edges[index].node;
      }
    }

    return sheetPart;
  };

  fillCsvArray = (row: *): * => {
    let array = this.csvArray;

    let index = -1;
    if (row[0] === "IMAGUS-FR-001a") {
      index = 0;
    } else if (row[0] === "IMAGUS-FR-001b") {
      index = 1;
    } else if (row[0] === "IMQ-SE-003a") {
      index = 2;
    } else if (row[0] === "IMQ-SE-003b") {
      index = 3;
    } else if (row[0].startsWith("IMQ-PR-003")) {
      index = 4;
    } else if (row[0] === "IMQ-SM-003") {
      index = 5;
    } else if (row[0] === "total") {
      index = 6;
    }

    array[index] = row;
  };

  getPartData = (
    item: *,
    value: number,
    priceCheck: boolean,
    partSum: number,
    DistributorDiscount: number,
    ResellerDiscount: number
  ): * => {
    const notes = getQuoteNotes(
      item.notes,
      item.min,
      item.max
      //item.partno.startsWith("IMQ-PR-003")
    );

    let retailPrice = "";
    if (priceCheck) {
      retailPrice = calculateRetailPrice(
        item.type,
        value,
        item.hasRange,
        item.priceRanges,
        item.price,
        partSum
      );

      if (item.partno.indexOf("IMQ-SE") > -1) {
        partSum += parseFloat(retailPrice);
      }
    }

    const retailPriceDisplay = displayRetailPrice(
      priceCheck,
      retailPrice,
      0,
      true
    );
    const disPriceDisplay = displayRetailPrice(
      priceCheck,
      retailPrice,
      DistributorDiscount,
      true
    );
    const resellerPriceDisplay = displayRetailPrice(
      priceCheck,
      retailPrice,
      ResellerDiscount,
      true
    );

    this.fillCsvArray([
      item.partno,
      value,
      retailPriceDisplay,
      disPriceDisplay,
      resellerPriceDisplay
    ]);

    return [
      item.partno,
      item.desctiotion,
      value,
      retailPriceDisplay,
      disPriceDisplay,
      resellerPriceDisplay,
      notes
    ];
  };

  render(): React$Element<*> {
    const classes = this.props.classes;
    const errors = this.state.errors;
    const loading = this.state.loading;

    let baseTable = [];
    let upgradeTabel = [];
    let renewTabel = [];
    let yearTabel = [];
    let totalTabel = [];

    if (this.state.licence) {
      // ---------------quote data-------------- //
      const authority = this.state.licence.company.inheritAuthority;
      const tier = this.state.licence.company.inheritTier;
      const distributorDiscount = authority
        ? tier.discounts.authorised.distributer
        : tier.discounts.standard.distributer;
      const resellerDiscount = authority
        ? tier.discounts.authorised.reseller
        : tier.discounts.standard.reseller;

      const priceSheet = this.props.priceSheet;
      const productSheet = this.state.licence.company.inheritProductSheet;
      const priceList = priceSheet && priceSheet.parts.edges;

      //base part
      baseTable = priceList
        .filter(
          (row: *): * =>
            row.node.partNo === "IMAGUS-FR-001a" ||
            row.node.partNo === "IMAGUS-FR-001b"
        )
        .map((row: *, i: number): * => {
          const sheetPart = this.getProductSheetPart(
            row.node.partNo,
            productSheet
          );
          const item = CompileDatabaseItemtoJsItem(row.node, sheetPart);

          return this.getPartData(
            item,
            row.node.partNo === "IMAGUS-FR-001a"
              ? this.state.licence.facebaseLimit
              : this.state.licence.noOfStreams,
            true,
            0,
            distributorDiscount,
            resellerDiscount
          );
        });

      const baseSum = getRetailSumFromTable(baseTable);
      const sumPrices = getTotalPrices(
        baseSum,
        distributorDiscount,
        resellerDiscount
      );

      /*  baseTable.push([
        "",
        "Total Existing Licence Fee",
        "",
        sumPrices[0],
        sumPrices[1],
        sumPrices[2],
        ""
      ]);
      this.fillCsvArray(
        [
          "total-1",
          "Total Existing Licence Fee",
          "",
          sumPrices[0],
          sumPrices[1],
          sumPrices[2],
          ""
        ],
        true
      ); */

      //upgrade part
      let upgradeSum;
      upgradeTabel = priceList
        .filter(
          (row: *): * =>
            row.node.partNo === "IMQ-SE-003a" ||
            row.node.partNo === "IMQ-SE-003b"
        )
        .map((row: *, i: number): * => {
          const sheetPart = this.getProductSheetPart(
            row.node.partNo,
            productSheet
          );
          const item = CompileDatabaseItemtoJsItem(row.node, sheetPart);

          const value =
            row.node.partNo === "IMQ-SE-003a"
              ? this.state.faceNo
              : this.state.streamNo;
          const price_check = checkItemPriceRange(value, item.min, item.max);

          return this.getPartData(
            item,
            value,
            price_check,
            0,
            distributorDiscount,
            resellerDiscount
          );
        });

      upgradeSum = getRetailSumFromTable(upgradeTabel);
      const sumPricesTwo = getTotalPrices(
        upgradeSum,
        distributorDiscount,
        resellerDiscount
      );

      /*  upgradeTabel.push([
        "",
        "Sub-Total New Licence Fee",
        "",
        sumPricesTwo[0],
        sumPricesTwo[1],
        sumPricesTwo[2],
        ""
      ]);
      this.fillCsvArray(
        [
          "total-2",
          "Sub-Total New Licence Fee",
          "",
          sumPricesTwo[0],
          sumPricesTwo[1],
          sumPricesTwo[2],
          ""
        ],
        true
      ); */

      //renew part
      /*  const existingExpiry = this.state.licence.extentDate
        ? this.state.licence.extentDate.format("DD/MMM/YYYY")
        : this.state.licence.expiryDate.format("DD/MMM/YYYY"); */

      /*  const newExpiry = this.state.expiryDate
        ? JsDateObjectDisplayDateOnly(this.state.expiryDate)
        : ""; */

      const renewFeeItem = priceList
        .filter(
          (row: *): * =>
            row.node.partNo ===
            (this.state.isOverDue ? "IMQ-PR-003b" : "IMQ-PR-003a")
        )
        .map((row: *, i: number): * => {
          const sheetPart = this.getProductSheetPart(
            row.node.partNo,
            productSheet
          );
          const item = CompileDatabaseItemtoJsItem(row.node, sheetPart);

          const value = this.state.renewDays;

          const adjustDays = isMomentBefore(
            StartDatetoEndDate(this.state.startDate, "days", 365),
            this.props.fetchRealExpiryDate(this.state.licence)
          );

          return this.getPartData(
            item,
            value,
            true,
            adjustDays ? upgradeSum : baseSum,
            distributorDiscount,
            resellerDiscount
          );
        });

      renewTabel.push(renewFeeItem[0]);
      /* renewTabel.push(
        [
          "",
          "Existing Licence Expiry Date",
          existingExpiry,
          "",
          "",
          "",
          "It refers to the expiry date of the existing licences."
        ],
        [
          "",
          "New Licences Start Date",
          JsDateObjectDisplayDateOnly(this.state.startDate),
          "",
          "",
          "",
          "Please specify a date >= current date."
        ],
        [
          "",
          "New Licences Expiry Date",
          newExpiry,
          "",
          "",
          "",
          "It is one year from the start date of the new licences."
        ],
        renewFeeItem[0]
      ); */

      const renewSum = getRetailSumFromTable(renewTabel);

      // year part
      yearTabel = priceList
        .filter((row: *): * => row.node.partNo === "IMQ-SM-003")
        .map((row: *, i: number): * => {
          const sheetPart = this.getProductSheetPart(
            row.node.partNo,
            productSheet
          );
          const item = CompileDatabaseItemtoJsItem(row.node, sheetPart);

          const value = this.state.yearNo;
          const price_check = checkItemPriceRange(value, item.min, item.max);

          return this.getPartData(
            item,
            value,
            price_check,
            baseSum + upgradeSum,
            distributorDiscount,
            resellerDiscount
          );
        });
      const yearSum = getRetailSumFromTable(yearTabel);

      //total part
      const sumPricesThree = getTotalPrices(
        upgradeSum + renewSum + yearSum,
        distributorDiscount,
        resellerDiscount
      );

      totalTabel.push([
        "Total Price",
        "New Licence Expires In " +
          JsDateObjectDisplayDateOnly(this.state.finalExpiryDate),
        "",
        sumPricesThree[0],
        sumPricesThree[1],
        sumPricesThree[2],
        ""
      ]);
      this.fillCsvArray([
        "total",
        null,
        sumPricesThree[0],
        sumPricesThree[1],
        sumPricesThree[2],
        ""
      ]);

      // ---------------quote data-------------- //
    }

    const page1Valid = !!this.state.licenceId && errors.licence === undefined;
    const page2Valid =
      this.state.faceNo !== null &&
      this.state.streamNo !== null &&
      errors.facebase === undefined &&
      errors.project === undefined;
    //const page3Valid = !!this.state.startDate;
    const page3Valid = this.state.yearNo !== null && errors.quote === undefined;
    const page4Valid =
      !!this.state.licenceId &&
      this.state.faceNo !== null &&
      this.state.streamNo !== null &&
      this.state.yearNo !== null &&
      Object.keys(errors).length === 0;

    let isNextDisabled;
    if (this.state.formIndex === 1) {
      isNextDisabled = !page1Valid;
    } else if (this.state.formIndex === 2) {
      isNextDisabled = !page2Valid;
    } else if (this.state.formIndex === 3) {
      isNextDisabled = !page3Valid;
    } else if (this.state.formIndex === 4) {
      isNextDisabled = !page4Valid;
    }

    let MainMenus = [{ label: "Select Existing Licence", value: 1 }];
    if (page1Valid) {
      MainMenus.push({ label: "Add Licence", value: 2 });
    }
    /* if (page2Valid) {
      MainMenus.push({ label: "New Start Date", value: 3 });
    } */
    if (page2Valid) {
      MainMenus.push({ label: "Additional Maintenance", value: 3 });
    }
    if (page3Valid) {
      MainMenus.push({ label: "Quote Preview", value: 4 });
    }

    return (
      <div className="app-wrapper">
        <div className="app-panel">
          <ButtonSelect
            value={this.state.formIndex}
            name="formIndex"
            buttons={MainMenus}
            handleChange={this.handlePageSwitch}
            noIndicator
          />
          <div className="app-panel-content">
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <Paper
                  className={classNames(classes.paper, {
                    [classes.hidden]: this.state.formIndex !== 1
                  })}
                >
                  <Grid container spacing={8}>
                    <Grid item xs={12}>
                      <StyledSelect
                        value={this.state.licenceId}
                        onChange={this.handleSelectChange}
                        name="licenceId"
                        label="Select Existing Perpetual Licence"
                        isRequired={true}
                        menus={this.props.LicenceList}
                        isStyled
                        isInvalid={errors.licence !== undefined}
                        isDisabled={this.props.isEdit}
                      />
                    </Grid>
                    {this.state.licence && (
                      <Grid item xs={12}>
                        <Paper className={classes.container}>
                          <Grid container spacing={8}>
                            <Grid item xs={12}>
                              <Typography variant="h5" className="form-text">
                                Selected Licence
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <LicenceItemRedux licence={this.state.licence} />
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                variant="h6"
                                className="right-margin-20"
                                style={{ display: "inline-flex" }}
                              >
                                Company Name
                              </Typography>
                              <span className="right-margin-10">
                                <CompanyNameReminder
                                  label={this.state.licence.company.name}
                                  className="right-margin-20"
                                />
                              </span>
                              <AuthorisedLabel
                                status={
                                  this.state.licence.company.inheritAuthority
                                    ? 1
                                    : -1
                                }
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <div
                  className={classNames(classes.paper, {
                    [classes.hidden]: this.state.formIndex !== 2
                  })}
                >
                  <Grid container spacing={24}>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        isRequired={true}
                        label="How many additional faces would you like to buy?"
                        name="faceNo"
                        value={
                          this.state.faceNo !== null ? this.state.faceNo : ""
                        }
                        onChange={this.handleQtyChange("faceNo")}
                        inpuType="number"
                        isInvalid={errors.facebase !== undefined}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        isRequired={true}
                        label="How many additional cameras/channels/streams would you like to buy?"
                        name="streamNo"
                        value={
                          this.state.streamNo !== null
                            ? this.state.streamNo
                            : ""
                        }
                        onChange={this.handleQtyChange("streamNo")}
                        inpuType="number"
                        isInvalid={errors.project !== undefined}
                      />
                    </Grid>
                  </Grid>
                </div>
              </Grid>
              <Grid item xs={12}>
                <div
                  className={classNames(classes.paper, {
                    [classes.hidden]: this.state.formIndex !== 3
                  })}
                >
                  <Grid container spacing={24}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth={true}>
                        <InputLabel
                          htmlFor="startDateOption"
                          required={true}
                          shrink={true}
                          classes={{
                            root: classes.greenLabel,
                            formControl: classes.labelFormControl,
                            focused: classes.labelFocused
                          }}
                        >
                          New Licence Start Date
                        </InputLabel>
                        <div className={classes.textFieldRoot}>
                          <SingleRadioButton
                            name="startDateOption"
                            value="false"
                            checkedValue={
                              this.state.startDateOption ? "true" : "false"
                            }
                            onChange={this.handleChange}
                            label={
                              "Today (" +
                              JsDateObjectDisplayDateOnly(
                                TodaytoStartTimeAppend(false)
                              ) +
                              ")"
                            }
                          />
                          {!this.state.isOverDue && (
                            <SingleRadioButton
                              name="startDateOption"
                              value="true"
                              checkedValue={
                                this.state.startDateOption ? "true" : "false"
                              }
                              onChange={this.handleChange}
                              label={
                                "Current Licence Expiry Date (" +
                                JsDateObjectDisplayDateOnly(
                                  this.props.fetchRealExpiryDate(
                                    this.state.licence
                                  )
                                ) +
                                ")"
                              }
                            />
                          )}
                        </div>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        isRequired={true}
                        label="How many additional maintenance and software upgrade services years you like to buy?"
                        name="yearNo"
                        value={
                          this.state.yearNo !== null ? this.state.yearNo : ""
                        }
                        onChange={this.handleQtyChange("yearNo")}
                        inpuType="number"
                        isInvalid={errors.quote !== undefined}
                      />
                    </Grid>
                  </Grid>
                </div>
              </Grid>
              <Grid item xs={12}>
                <div
                  className={classNames(classes.paper, {
                    [classes.hidden]: this.state.formIndex !== 4
                  })}
                >
                  {this.state.licence && (
                    <Grid container spacing={8}>
                      <Grid item xs={12}>
                        <UpgradeQuoteTable
                          authorised_reseller={
                            this.state.licence.company.inheritAuthority
                          }
                          companyType={this.props.companyType}
                          create_date={new Date()}
                          baseTable={baseTable}
                          upgradeTabel={upgradeTabel}
                          renewTabel={renewTabel}
                          yearTabel={yearTabel}
                          totalTabel={totalTabel}
                          tier_name={
                            this.state.licence.company.inheritTier &&
                            this.state.licence.company.inheritTier.name
                          }
                          productSheetName={
                            this.state.licence.company.inheritProductSheet &&
                            this.state.licence.company.inheritProductSheet
                              .description
                          }
                          startDate={this.state.startDate}
                        />
                      </Grid>
                    </Grid>
                  )}
                </div>
              </Grid>
              <Grid item xs={12}>
                <ErrosDisplay errors={errors} form="upgrade quote" />
              </Grid>
              <Grid item xs={12}>
                <div className={classes.bottom}>
                  <span className={classes.leftButton}>
                    {this.state.formIndex !== 1 && (
                      <CancelButton
                        noMargin
                        title="Previous"
                        onClick={(): * =>
                          this.handlePageSwitch(
                            "formIndex",
                            parseInt(this.state.formIndex) - 1
                          )
                        }
                      />
                    )}
                  </span>
                  <span className={classes.rightButton}>
                    {this.state.formIndex === 4 ? (
                      <SubmitButton
                        buttonClass="dark-btn"
                        loading={loading}
                        disabled={isNextDisabled}
                        onClick={(): * => this.submit()}
                      />
                    ) : (
                      <SubmitButton
                        buttonClass="dark-btn"
                        loading={false}
                        disabled={isNextDisabled}
                        onClick={(): * =>
                          this.handlePageSwitch(
                            "formIndex",
                            parseInt(this.state.formIndex) + 1
                          )
                        }
                        title="Next"
                      />
                    )}
                  </span>
                </div>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(UpgradeQuoteForm);
